import React, { useState, useContext, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";

import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import { versionSystem, nomeEmpresa } from "../../../package.json";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import logoLight from "../../assets/logo.png";
import {
  TranslateOutlined,
  WbSunnyOutlined,
  NightsStayOutlined,
} from "@material-ui/icons";
import { IconButton, Menu, MenuItem } from "@material-ui/core";
import LanguageControl from "../../components/LanguageControl";
import ColorModeContext from "../../layout/themeContext";

/** ==== Rodapé ==== */
const Copyright = () => {
  return (
    <Typography
      variant="body2"
      align="center"
      style={{ color: "#FFFFFF" }}
    >
      {"Copyright "}
      <span style={{ color: "#FFFFFF" }}>
        {nomeEmpresa} - {new Date().getFullYear()}
      </span>
      {"."}
    </Typography>
  );
};

/**
 * ==== Estilos Floating UI ====
 * Cores sólidas, sombras profundas, elementos flutuantes
 * Sem glassmorphism, sem scroll
 */
const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";

  return {
    // Container principal - ocupa viewport inteira, sem scroll
    root: {
      width: "100vw",
      height: "100vh",
      overflow: "hidden",
      backgroundColor: isLight ? theme.palette.primary.main : "#1c1c1c",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      // iPad/tablet touch: fixa o container ao viewport para evitar deslocamento
      // do body ao focar campos (mesmo com teclado físico); permite scroll interno.
      "@media (pointer: coarse) and (min-width: 600px)": {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        height: "auto",
        overflowY: "auto",
      },
      [theme.breakpoints.down("sm")]: {
        height: "auto",
        minHeight: "100vh",
        overflowY: "auto",
        justifyContent: "flex-start",
        paddingTop: "15vh",
      },
    },

    // Card flutuante com sombras profundas
    paper: {
      backgroundColor: "#ffffff",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "48px 40px",
      borderRadius: theme.neoUI?.floating.card?.borderRadius || 20,
      boxShadow: theme.neoUI?.floating.card?.boxShadow || "0 20px 60px -10px rgba(0, 0, 0, 0.15)",
      border: theme.neoUI?.floating.card?.border || "1px solid rgba(0, 0, 0, 0.04)",
      transition: "transform 0.3s ease, box-shadow 0.3s ease",
      maxWidth: 400,
      width: "100%",
      "&:hover": {
        boxShadow: theme.neoUI?.floating.card?.hoverShadow || "0 28px 70px -10px rgba(0, 0, 0, 0.18)",
      },
      [theme.breakpoints.down("sm")]: {
        padding: "32px 24px",
      },
    },

    // Formulário
    form: {
      width: "100%",
      marginTop: theme.spacing(2),
    },

    // Botão com sombra verde
    submit: {
      ...theme.neoUI.buttons.action.root,
      margin: theme.spacing(3, 0, 2),
      borderRadius: 12,
      padding: "14px",
      fontSize: "1rem",
      height: "auto",
      minHeight: "auto",
      transition: "all 0.3s ease-in-out",
      "&:hover": {
        ...theme.neoUI.buttons.action.root["&:hover"],
      },
    },

    // Controles de idioma e tema (posicionados no topo)
    languageControl: {
      position: "absolute",
      top: 16,
      left: 16,
      [theme.breakpoints.down("sm")]: {
        top: "calc(var(--safe-area-top) + 8px)",
      },
      "html[data-tablet-portrait] &": { top: "calc(env(safe-area-inset-top, 0px) + 8px)" },
    },
    themeControl: {
      position: "absolute",
      top: 16,
      right: 16,
      [theme.breakpoints.down("sm")]: {
        top: "calc(var(--safe-area-top) + 8px)",
      },
      "html[data-tablet-portrait] &": { top: "calc(env(safe-area-inset-top, 0px) + 8px)" },
    },

    // Campos de entrada com sombra interna
    loginFieldRoot: {
      backgroundColor: "#ffffff",
      borderRadius: 10,
      boxShadow: theme.neoUI?.floating.input?.boxShadow || "inset 0 2px 4px rgba(0, 0, 0, 0.04)",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(0, 0, 0, 0.12)",
        borderWidth: 1,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
      "&.Mui-focused": {
        boxShadow: theme.neoUI?.floating.input?.focusShadow || "0 0 0 3px rgba(20, 164, 92, 0.15)",
      },
      "& input:-webkit-autofill": {
        WebkitTextFillColor: "#000 !important",
        WebkitBoxShadow: "0 0 0 1000px #fff inset !important",
      },
    },
    loginInput: {
      color: "#000",
      padding: "14px 16px",
      "&::placeholder": { color: "#9EA3AA", opacity: 1 },
    },

    // Footer com links legais - posição absoluta para não causar scroll
    footer: {
      position: "absolute",
      bottom: 24,
      left: 0,
      right: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 12,
      // iPad: sai do absolute para participar do fluxo de scroll interno
      "@media (pointer: coarse) and (min-width: 600px)": {
        position: "relative",
        bottom: "auto",
        marginTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
      },
      [theme.breakpoints.down("sm")]: {
        position: "relative",
        bottom: "auto",
        marginTop: "auto",
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
      },
    },
    legalLinks: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: 8,
      [theme.breakpoints.down("sm")]: {
        flexWrap: "wrap",
        gap: 4,
      },
    },
    legalLink: {
      color: "#FFFFFF",
      opacity: 0.9,
      transition: "opacity 0.2s ease",
      textAlign: "center",
      "&:hover": {
        opacity: 1,
      },
      [theme.breakpoints.down("sm")]: {
        fontSize: "0.75rem",
      },
    },
    legalSeparator: {
      color: "#FFFFFF",
      opacity: 0.6,
      [theme.breakpoints.down("sm")]: {
        display: "none",
      },
    },

    // Logo
    logo: {
      width: "70%",
      maxWidth: 200,
      marginBottom: theme.spacing(2),
    },
  };
});

const Login = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isLight = theme.palette.type === "light";
  const colorMode = useContext(ColorModeContext);

  const [user, setUser] = useState({ email: "", password: "" });

  // Idiomas
  const [anchorElLanguage, setAnchorElLanguage] = useState(null);
  const [menuLanguageOpen, setMenuLanguageOpen] = useState(false);

  const { handleLogin } = useContext(AuthContext);
  const [submitting, setSubmitting] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const handleChangeInput = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handlSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await handleLogin(user);
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  };

  const handlemenuLanguage = (event) => {
    setAnchorElLanguage(event.currentTarget);
    setMenuLanguageOpen(true);
  };

  const handleCloseMenuLanguage = () => {
    setAnchorElLanguage(null);
    setMenuLanguageOpen(false);
  };

  const handleToggleTheme = () => {
    if (colorMode?.toggleColorMode) {
      colorMode.toggleColorMode();
    }
  };

  return (
    <div className={classes.root} data-pwa-top-surface>
      {/* Seletor de idioma */}
      <div className={classes.languageControl}>
        <IconButton edge="start">
          <TranslateOutlined
            aria-label="language selector"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handlemenuLanguage}
            style={{ color: "#FFFFFF" }}
          />
        </IconButton>
        <Menu
          id="menu-appbar-language"
          anchorEl={anchorElLanguage}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          open={menuLanguageOpen}
          onClose={handleCloseMenuLanguage}
        >
          <MenuItem>
            <LanguageControl />
          </MenuItem>
        </Menu>
      </div>

      {/* Botão de tema */}
      <div className={classes.themeControl}>
        <IconButton
          edge="end"
          onClick={handleToggleTheme}
          aria-label="toggle theme"
        >
          {isLight ? (
            <NightsStayOutlined style={{ color: "#FFFFFF" }} />
          ) : (
            <WbSunnyOutlined style={{ color: "#FFFFFF" }} />
          )}
        </IconButton>
      </div>

      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <div className={classes.paper}>
          <img
            className={classes.logo}
            src={logoLight}
            alt="NeoZapp"
          />

          <form className={classes.form} noValidate onSubmit={handlSubmit}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              placeholder={`${i18n.t("login.form.email")} *`}
              name="email"
              value={user.email}
              onChange={handleChangeInput}
              autoComplete="email"
              autoFocus
              inputProps={{
                autoCapitalize: "none",
                autoCorrect: "off",
                spellCheck: false,
              }}
              InputProps={{
                notched: false,
                classes: {
                  root: classes.loginFieldRoot,
                  input: classes.loginInput,
                },
              }}
            />

            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              placeholder={`${i18n.t("login.form.password")} *`}
              type="password"
              id="password"
              value={user.password}
              onChange={handleChangeInput}
              autoComplete="current-password"
              InputProps={{
                notched: false,
                classes: {
                  root: classes.loginFieldRoot,
                  input: classes.loginInput,
                },
              }}
            />

            <Grid container justifyContent="flex-end" style={{ marginTop: -6 }}>
              <Grid item>
                <Link
                  component={RouterLink}
                  to="/forgetpsw"
                  variant="body2"
                >
                  {i18n.t("login.buttons.forgot")}
                </Link>
              </Grid>
            </Grid>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {i18n.t("login.buttons.submit")}
            </Button>

            <Grid container justifyContent="center">
              <Grid item>
                <Link
                  href="#"
                  variant="body2"
                  component={RouterLink}
                  to="/signup"
                >
                  {i18n.t("login.buttons.register")}
                </Link>
              </Grid>
            </Grid>
          </form>
        </div>
      </Container>

      {/* Footer com links legais */}
      <div className={classes.footer}>
        <div className={classes.legalLinks}>
          <Link
            component={RouterLink}
            to="/termos"
            variant="body2"
            className={classes.legalLink}
          >
            {i18n.t("legal.terms")}
          </Link>
          <span className={classes.legalSeparator}>|</span>
          <Link
            component={RouterLink}
            to="/privacidade"
            variant="body2"
            className={classes.legalLink}
          >
            {i18n.t("legal.privacy")}
          </Link>
          <span className={classes.legalSeparator}>|</span>
          <Link
            component={RouterLink}
            to="/exclusao-dados"
            variant="body2"
            className={classes.legalLink}
          >
            {i18n.t("legal.deletion")}
          </Link>
        </div>
        <Copyright />
      </div>
    </div>
  );
};

export default Login;
